import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX,
  Cloud,
  Waves,
  TreePine,
  CloudLightning,
  Droplet,
  Moon,
  Lock
} from "lucide-react";
import { useAuth } from "@/App";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasAccess, isFreeSoundscape, MEMBERSHIP_URL } from "@/lib/subscriptionAccess";

// Free tier soundscapes: rain, ocean-waves
const FREE_SOUNDSCAPE_IDS = ['rain', 'ocean'];

// Soundscape data with Cloudflare R2 audio URLs (no subfolder)
const SOUNDSCAPES = [
  {
    id: "rain",
    title: "Rain",
    icon: Cloud,
    color: "#6B8CAE",
    description: "Let your thoughts dissolve",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Light%20Gentle%20Rain%20Part%201.mp3",
    freeAccess: true
  },
  {
    id: "ocean",
    title: "Ocean Waves",
    icon: Waves,
    color: "#4A90A4",
    description: "Breathe with the waves",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Birds%20With%20Ocean%20Waves%20on%20the%20Beach.mp3",
    freeAccess: true
  },
  {
    id: "forest",
    title: "Forest Birds",
    icon: TreePine,
    color: "#5D8A66",
    description: "Return to stillness",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Relaxing%20Nature%20Sounds%20-%20Birdsong%20Sound.mp3",
    freeAccess: false
  },
  {
    id: "thunder",
    title: "Thunder Storm",
    icon: CloudLightning,
    color: "#7A6B8A",
    description: "Find calm in the chaos",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Gentle%20Rain%20and%20Thunder.mp3",
    freeAccess: false
  },
  {
    id: "river",
    title: "River Stream",
    icon: Droplet,
    color: "#5B9EA6",
    description: "Let tension flow away",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/Relaxing%20Nature%20Sounds%20-%20Trickling%20Stream%20Sounds%20%26%20Birds.mp3",
    freeAccess: false
  },
  {
    id: "crickets",
    title: "Night Crickets",
    icon: Moon,
    color: "#6A5A7A",
    description: "Settle into quiet",
    audioUrl: "https://pub-d54bc428c5344e09b63305c4532156ab.r2.dev/ambience-crickets-chirping-in-very-light-rain-followed-by-gentle-rolling-thunder-10577.mp3",
    freeAccess: false
  }
];

// Individual Soundscape Card
function SoundscapeCard({ soundscape, isPlaying, onTogglePlay, volume, onVolumeChange, isLocked }) {
  const Icon = soundscape.icon;
  
  const handleClick = () => {
    if (isLocked) {
      window.open(MEMBERSHIP_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    onTogglePlay(soundscape);
  };
  
  return (
    <Card 
      className={`glass-card overflow-hidden transition-all ${isPlaying ? 'ring-2 ring-[#a69373]/50' : ''} ${isLocked ? 'opacity-70' : ''}`}
      data-testid={`soundscape-${soundscape.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div 
            className={`w-10 h-10 rounded-xl flex items-center justify-center relative ${isPlaying ? 'animate-pulse' : ''}`}
            style={{ backgroundColor: `${soundscape.color}20` }}
          >
            <Icon className="w-5 h-5" style={{ color: soundscape.color }} />
            {isPlaying && (
              <div 
                className="absolute inset-0 rounded-xl animate-ping opacity-30"
                style={{ backgroundColor: soundscape.color }}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-white font-medium text-sm block">{soundscape.title}</span>
            <span className="text-white/40 text-xs">
              {isLocked ? "Upgrade to unlock" : soundscape.description}
            </span>
          </div>
          <button
            onClick={handleClick}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
              isLocked
                ? 'bg-white/5 hover:bg-white/10'
                : isPlaying 
                  ? 'bg-[#a69373] hover:bg-[#d4b47a]' 
                  : 'bg-white/10 hover:bg-white/20'
            }`}
            data-testid={`soundscape-${soundscape.id}-play`}
          >
            {isLocked ? (
              <Lock className="w-4 h-4 text-white/50" />
            ) : isPlaying ? (
              <Pause className="w-5 h-5 text-[#0B2821]" />
            ) : (
              <Play className="w-5 h-5 text-white ml-0.5" />
            )}
          </button>
        </div>
        
        {isPlaying && !isLocked && (
          <div className="flex items-center gap-2 mt-2 animate-fade-in">
            <VolumeX className="w-4 h-4 text-white/50" />
            <Slider
              value={[volume * 100]}
              min={0}
              max={100}
              step={1}
              onValueChange={(v) => onVolumeChange(v[0] / 100)}
              className="flex-1"
            />
            <Volume2 className="w-4 h-4 text-white/50" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Main Soundscapes Section Component - handles audio directly (no audioManager)
export function SoundscapesSection() {
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [volume, setVolume] = useState(0.08);
  const audioRefs = useRef({});
  const { user } = useAuth();
  const { subscription } = useSubscription();
  
  // Check if user has full soundscape access (any paid tier) - pass user for admin bypass
  const hasFullAccess = hasAccess(subscription.type, 'monthly', user);

  // Create audio elements for each soundscape on mount
  useEffect(() => {
    SOUNDSCAPES.forEach(soundscape => {
      const audio = new Audio();
      audio.preload = "auto";
      audio.loop = true;
      audio.volume = 0.08;
      audio.src = soundscape.audioUrl;
      audioRefs.current[soundscape.id] = audio;
    });

    // Cleanup on unmount
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        audio.pause();
        audio.src = "";
      });
      audioRefs.current = {};
    };
  }, []);

  // Stop all audio helper
  const stopAllAudio = () => {
    Object.values(audioRefs.current).forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
  };

  const handleTogglePlay = async (soundscape) => {
    const audio = audioRefs.current[soundscape.id];
    if (!audio) return;

    // If this soundscape is already playing, stop it
    if (currentPlaying === soundscape.id) {
      audio.pause();
      audio.currentTime = 0;
      setCurrentPlaying(null);
      return;
    }

    // Stop all other audio first
    stopAllAudio();

    // Set volume and loop before playing
    audio.volume = volume;
    audio.loop = true;

    // Try to play with error handling
    try {
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        await playPromise;
      }
      setCurrentPlaying(soundscape.id);
    } catch (error) {
      console.error('Soundscape audio error:', error);
      toast.error("Audio unavailable — check your connection.", { duration: 3000 });
      setCurrentPlaying(null);
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    // Update volume on currently playing audio
    if (currentPlaying && audioRefs.current[currentPlaying]) {
      audioRefs.current[currentPlaying].volume = newVolume;
    }
  };

  return (
    <div id="soundscapes" className="space-y-4" data-testid="soundscapes-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#a69373]/20 flex items-center justify-center">
          <Waves className="w-5 h-5 text-[#a69373]" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-lg" style={{ color: '#a69373' }}>Soundscapes</h2>
          <p className="text-sm" style={{ color: 'rgba(166, 147, 115, 0.5)' }}>Ambient sounds for focus & calm</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {SOUNDSCAPES.map(soundscape => {
          // Free users can access freeAccess soundscapes, paid users can access all
          const isLocked = !hasFullAccess && !soundscape.freeAccess;
          return (
            <SoundscapeCard
              key={soundscape.id}
              soundscape={soundscape}
              isPlaying={currentPlaying === soundscape.id}
              onTogglePlay={handleTogglePlay}
              volume={volume}
              onVolumeChange={handleVolumeChange}
              isLocked={isLocked}
            />
          );
        })}
      </div>
    </div>
  );
}

export default SoundscapesSection;
